import {FormProvider, useForm} from "react-hook-form";
import './index.css'
import {useSelector} from "react-redux";
import {useCallback, useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import toast from "react-hot-toast";
import HeaderLayout from "@/components/common/CustomLayout/HeaderLayout";
import Button from "@/components/common/Button";
import Pricing from "@/pages/Venues/Edit/Pricing";
import Information from "@/pages/Venues/Edit/Information";
import Court from "@/pages/Venues/Edit/Court";
import Setting from "@/pages/Venues/Edit/Setting";
import {RootState, useAppDispatch} from "@/store";
import {
  getVenueDetail,
  createVenue,
  editVenue
} from "@/store/venue-detail/action";
import {venueDetailActions} from "@/store/venue-detail/slice";
import PricingModal from "@/pages/Venues/Edit/PricingModal";
import {validateCourt, validateHourRuleSetting} from "@/utils/validation";

const validateVenueData = (venue: Venue) => {
  let errorMessage = null
  if (!venue.name) {
    errorMessage = 'Venue name can not be empty'
  } else if (!venue.pic_phone_number) {
    errorMessage = 'PIC phone number can not be empty'
  } else if (!venue.address) {
    errorMessage = 'Venue address can not be empty'
  } else if (!venue.address_long || !venue.address_lat) {
    errorMessage = 'Venue location pin can not be empty'
  } else if (!venue.prices.length) {
    errorMessage = 'Prices must be filled'
  } else if (!venue.courts.length) {
    errorMessage = 'Courts must be filled'
  } else if (!venue.courts.every(validateCourt)) {
    errorMessage = 'All courts must have price and name'
  } else if (venue.hour_rule_setting) {
    if (!venue.hour_rule_setting_details.length) {
      errorMessage = 'Setting Periode must be filled if hour rule settings is true'
    } else if (!venue.hour_rule_setting_details.every(validateHourRuleSetting)) {
      errorMessage = 'Setting Periode is not correct. start time must be less than or equal end time'
    }
  }

  if (errorMessage) {
    toast.error(errorMessage)
  }
  return !errorMessage
}

const VenueDetail= () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { venue, show, currentPriceIndex, loadingAction } = useSelector((state: RootState) =>  state.venueDetail)
  const venueDetailMethods = useForm<Venue>({
    defaultValues: venue
  })
  const { getValues, setValue } = venueDetailMethods
  const { venueId } = useParams()
  
  const headerTitle = venueId ? 'Edit Venue' : 'Create Venue'
  
  const closeModal = useCallback(() => {
    const payload: ShowModalActionPayload = {
      show: false,
      currentPrice: null,
      currentPriceIndex: -1
    }
    dispatch(venueDetailActions.showPriceModal(payload))
  }, [])
  
  const getCleanHourRule = (dirtyHourRule): HourRule => ({
    start_time: parseInt(dirtyHourRule.start_time, 10),
    end_time: parseInt(dirtyHourRule.end_time, 10)
  })
  
  const getCleanVenueData = () => {
    const venueData = venueDetailMethods.getValues()
    const taxFee = Number(venueData.tax_fee)
    const cleanHourRules = venueData.hour_rule_setting_details.map(getCleanHourRule)
    return {
      ...venueData,
      tax_fee: taxFee,
      hour_rule_setting_details: cleanHourRules
    }
  }

  const onSuccessSubmit = () => {
    navigate('/dashboard/venue')
  }
  
  const onSubmit = (e) => {
    e.preventDefault()
    const venueRequestData = getCleanVenueData()
    
    if (!validateVenueData(venueRequestData)) {
      return
    }
    if (venueId) {
      dispatch(editVenue(venueRequestData))
        .then(onSuccessSubmit)
    } else {
      dispatch(createVenue(venueRequestData))
        .then(onSuccessSubmit)
    }
  }
  
  const addPricingClass = (pricingClass: Price) => {
    const currentPrices = getValues('prices')
    
    if (currentPriceIndex > -1) {
      currentPrices[currentPriceIndex] = pricingClass
      setValue('prices', [...currentPrices])
    } else {
      setValue('prices', [...currentPrices, pricingClass])
    }
    
    closeModal()
  }
  
  useEffect(() => {
    if (venueId) {
      dispatch(getVenueDetail(venueId))
    } else {
      dispatch(venueDetailActions.resetVenueDetail())
    }
  }, [])

  useEffect(() => {
    if (venue) venueDetailMethods.reset(venue)
  }, [venue])

  return (
    <>
      {
        venueId && loadingAction ? <></>
          : <FormProvider {...venueDetailMethods}>
            <form onSubmit={venueDetailMethods.handleSubmit(onSubmit)}>
              <HeaderLayout
                title={headerTitle}
                rightComponent={
                  <Button
                    onClick={onSubmit}
                    type="submit"
                    variant="primary"
                    className="whitespace-nowrap flex gap-2 px-12 py-2 justify-center items-center text-jsm font-medium"
                  >
                    Submit
                  </Button>}
              >
                <div className="grid grid-cols-2 w-full h-full gap-6">
                  <Information/>
                  <Pricing/>
                  <Court/>
                  <Setting/>
                </div>
              </HeaderLayout>
            </form>
          </FormProvider>
      }
      {show &&
        <PricingModal
          onSubmit={addPricingClass}
          closeModal={closeModal}
        />
      }
    </>
  )
}

export default VenueDetail;